import { PlatformType, useConvertToRichTextMutation } from '@/lib/features/seo/seoToolsApi'
import { ChevronDown, Copy, FileCode, Loader2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'

export interface RichTextCopyButtonProps {
  /** The markdown content to convert and copy */
  markdown: string
  /** Optional HTML content for direct copy (bypasses conversion) */
  html?: string
  /** Optional custom button text */
  buttonText?: string
  /** Optional size variant */
  size?: 'sm' | 'md' | 'lg'
  /** Optional className for custom styling */
  className?: string
}

/**
 * Reusable button component for copying markdown content as rich text to clipboard
 * Supports Shopify, WordPress, and Framer platforms with a dropdown selection
 */
export function RichTextCopyButton({
  markdown,
  html,
  buttonText = 'Copy as Rich Text',
  size = 'md',
  className = '',
}: RichTextCopyButtonProps) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [selectedPlatform, setSelectedPlatform] = useState<PlatformType>('shopify')
  const dropdownRef = useRef<HTMLDivElement>(null)

  const [convertToRichText, { isLoading }] = useConvertToRichTextMutation()

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // Size configurations
  const sizeClasses = {
    sm: {
      button: 'px-2 py-1 text-xs',
      icon: 'h-3 w-3',
      chevron: 'h-3 w-3',
    },
    md: {
      button: 'px-3 py-2 text-sm',
      icon: 'h-4 w-4',
      chevron: 'h-4 w-4',
    },
    lg: {
      button: 'px-4 py-2 text-base',
      icon: 'h-5 w-5',
      chevron: 'h-5 w-5',
    },
  }

  const classes = sizeClasses[size]

  /**
   * Copy HTML content to clipboard using the Clipboard API
   * Includes both HTML and plain text representations
   */
  async function copyHtmlToClipboard(html: string, plainText: string): Promise<boolean> {
    try {
      // Modern Clipboard API with HTML support
      if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
        const htmlBlob = new Blob([html], { type: 'text/html' })
        const textBlob = new Blob([plainText], { type: 'text/plain' })

        await navigator.clipboard.write([
          new ClipboardItem({
            'text/html': htmlBlob,
            'text/plain': textBlob,
          }),
        ])
        return true
      }

      // Fallback using execCommand with copy event interception
      return copyHtmlFallback(html, plainText)
    } catch (error) {
      console.warn('Primary clipboard method failed, using fallback:', error)
      return copyHtmlFallback(html, plainText)
    }
  }

  /**
   * Fallback clipboard method for older browsers
   */
  function copyHtmlFallback(html: string, plainText: string): boolean {
    let success = false

    function onCopy(event: ClipboardEvent) {
      event.preventDefault()
      event.clipboardData?.setData('text/html', html)
      event.clipboardData?.setData('text/plain', plainText)
      success = true
    }

    document.addEventListener('copy', onCopy)
    try {
      // Create a temporary element for selection
      const el = document.createElement('span')
      el.textContent = '\u200B' // zero-width space
      el.style.cssText = 'position:fixed;left:-9999px;opacity:0'
      document.body.appendChild(el)

      // Select and copy
      const range = document.createRange()
      range.selectNodeContents(el)
      const sel = window.getSelection()
      sel?.removeAllRanges()
      sel?.addRange(range)

      document.execCommand('copy')

      sel?.removeAllRanges()
      document.body.removeChild(el)
    } finally {
      document.removeEventListener('copy', onCopy)
    }
    return success
  }

  /**
   * Handle the copy action for a specific platform
   */
  async function handleCopy(platform: PlatformType) {
    if (!markdown.trim()) {
      toast.error('No content to copy')
      return
    }

    try {
      // For Framer and WordPress, use direct HTML copy if available
      if ((platform === 'framer' || platform === 'wordpress') && html && html.trim()) {
        // Generate plain text from HTML for clipboard fallback
        const tempDiv = document.createElement('div')
        tempDiv.innerHTML = html
        const plainText = tempDiv.textContent || tempDiv.innerText || ''

        // Copy to clipboard directly
        const success = await copyHtmlToClipboard(html, plainText)

        if (success) {
          const platformName = platform === 'framer' ? 'Framer' : 'WordPress'
          toast.success(`✅ Rich text copied for ${platformName}! Paste into your editor.`)

          console.log('Direct HTML copy successful:', {
            platform,
            htmlLength: html.length,
            plainTextLength: plainText.length,
          })
        } else {
          toast.error('Failed to copy to clipboard')
        }

        setIsDropdownOpen(false)
        return
      }

      // For Shopify (or when HTML not available), use API conversion
      const result = await convertToRichText({
        markdown,
        platform,
      }).unwrap()

      // Copy to clipboard
      const success = await copyHtmlToClipboard(result.data.html, result.data.plainText)

      if (success) {
        const platformName =
          platform === 'shopify' ? 'Shopify' : platform === 'wordpress' ? 'WordPress' : 'Framer'
        toast.success(`✅ Rich text copied for ${platformName}! Paste into your editor.`)

        // Track the successful copy
        console.log('Rich text copy successful:', {
          platform,
          originalLength: result.meta.originalLength,
          htmlLength: result.meta.htmlLength,
          plainTextLength: result.meta.plainTextLength,
        })
      } else {
        toast.error('Failed to copy to clipboard')
      }
    } catch (error: any) {
      console.error('Rich text conversion failed:', error)

      // Handle specific error messages
      let errorMessage = 'Failed to convert content'
      if (error?.data?.error) {
        errorMessage = error.data.error
        if (error.data.details) {
          console.error('Error details:', error.data.details)
        }
      }

      toast.error(`❌ ${errorMessage}`)
    } finally {
      setIsDropdownOpen(false)
    }
  }

  /**
   * Handle direct copy with selected platform
   */
  async function handleDirectCopy() {
    await handleCopy(selectedPlatform)
  }

  const platformOptions = [
    {
      value: 'shopify' as PlatformType,
      label: 'Shopify',
      description: 'Optimized for Shopify blog editor',
      icon: '🛒',
      available: true,
    },
    {
      value: 'wordpress' as PlatformType,
      label: 'WordPress',
      description: 'Optimized for WordPress editor',
      icon: '📝',
      available: true,
    },
    {
      value: 'framer' as PlatformType,
      label: 'Framer',
      description: 'Optimized for Framer CMS rich text fields',
      icon: '🎨',
      available: true,
    },
  ]

  return (
    <div className={`relative inline-flex ${className}`} ref={dropdownRef}>
      {/* Main button group */}
      <div className="inline-flex rounded-lg border border-blue-300 bg-blue-600 shadow-sm">
        {/* Copy button */}
        <button
          onClick={handleDirectCopy}
          disabled={isLoading}
          className={`inline-flex items-center gap-2 rounded-l-lg font-medium text-white transition-all duration-200 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50 ${classes.button} `}
        >
          {isLoading ? (
            <Loader2 className={`${classes.icon} animate-spin`} />
          ) : (
            <FileCode className={classes.icon} />
          )}
          {isLoading ? 'Converting...' : buttonText}
        </button>

        {/* Dropdown toggle */}
        <button
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          disabled={isLoading}
          className={`rounded-r-lg border-l border-blue-500 px-2 text-white transition-all duration-200 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50`}
        >
          <ChevronDown
            className={`${classes.chevron} transition-transform duration-200 ${
              isDropdownOpen ? 'rotate-180' : ''
            }`}
          />
        </button>
      </div>

      {/* Dropdown menu */}
      {isDropdownOpen && (
        <div className="absolute left-0 top-full z-50 mt-2 w-64 rounded-lg border border-gray-200 bg-white shadow-lg">
          <div className="p-2">
            <div className="mb-2 px-3 py-2 text-xs font-medium uppercase tracking-wider text-gray-500">
              Select Platform
            </div>
            {platformOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => {
                  if (option.available) {
                    setSelectedPlatform(option.value)
                    handleCopy(option.value)
                  }
                }}
                disabled={!option.available || isLoading}
                className={`flex w-full items-center gap-3 rounded-md px-3 py-2 text-left transition-colors duration-150 ${
                  option.available
                    ? 'cursor-pointer text-gray-900 hover:bg-blue-50'
                    : 'cursor-not-allowed text-gray-400'
                } ${
                  selectedPlatform === option.value && option.available
                    ? 'bg-blue-100 ring-1 ring-blue-500'
                    : ''
                } `}
              >
                <span className="text-lg">{option.icon}</span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium">{option.label}</div>
                  <div className="text-xs text-gray-500">{option.description}</div>
                </div>
                {selectedPlatform === option.value && option.available && (
                  <Copy className="h-3 w-3 text-blue-600" />
                )}
              </button>
            ))}
          </div>

          {/* Footer info */}
          <div className="border-t border-gray-100 px-3 py-2">
            <div className="text-xs text-gray-500">
              Rich text includes formatting like bold, italic, headings, and links
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
